/**
 * AI Analysis Service Types
 *
 * Types for AI document analysis and entity creation workflow.
 */

import type {
  ApplyProposedHandover,
  CurrentHandover,
  HandoverHandlerOption,
  HandoversApplyOutcome,
  PreviewProposedHandover
} from '@/lib/services/matter-handling/types';
import type { NonProvingReason } from '@/lib/constants';
import type { AnalysisFailureReasonCode } from '@/lib/services/files/types';

/**
 * Extracted matter data from AI consolidation (existing type)
 * This is the simplified format returned by the consolidation endpoint
 */
export interface ExtractedMatterData {
  // Decedent information
  decedentFirstName?: string;
  decedentMiddleName?: string;
  decedentLastName?: string;
  decedentDateOfBirth?: string;
  decedentDateOfDeath?: string;
  decedentAddress?: string;
  decedentCity?: string;
  decedentState?: string;
  decedentZipCode?: string;
  decedentCountry?: string;

  // Executor/LPR information
  executorFirstName?: string;
  executorLastName?: string;
  executorEmail?: string;
  executorPhone?: string;

  // Matter metadata
  hasWill?: boolean;
  estimatedEstateValue?: number;
  analysisConfidence?: number;
  warnings?: string[];
}

/**
 * Postal address structure (matches backend PostalAddress)
 */
export interface PostalAddress {
  streetAddress?: string | null;
  suburb?: string | null;
  state?: string | null;
  postcode?: string | null;
  country?: string | null;
}

/**
 * Matter data from preview-changes endpoint
 */
export interface ExtractedMatterDataFromPreview {
  lawFirmInternalReference?: string;
  decedentTitle?: string;
  decedentFirstName?: string;
  decedentMiddleName?: string;
  decedentLastName?: string;
  decedentDateOfBirth?: string; // ISO 8601 date string
  decedentDateOfDeath?: string; // ISO 8601 date string
  decedentAddress?: PostalAddress;
  deathCertificateFileId?: number;
  willFileId?: number;
  grantOfRepresentationFileId?: number;
  notes?: string;
  /**
   * AI-extraction provenance carried through the create-matter wizard review
   * (LF-5597). Threaded from the preview diff's `matter.source`/`.confidence`
   * so the review renders the "Extracted from …" footnote. Display-only for the
   * deceased/matter (no matter-level provenance store on apply).
   */
  source?: QuoteSource;
  confidence?: ConfidenceLevel;
}

/**
 * Contact data from preview-changes endpoint
 */
export interface ExtractedContact {
  title?: string;
  firstName: string;
  middleName?: string | null;
  lastName: string;
  organisationName?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  dateOfBirth?: string | null;
  isDeceased?: boolean | null;
  dateOfDeath?: string | null;
  address?: PostalAddress | null;
  isLegalPersonalRepresentative: boolean;
  legalPersonalRepresentativeRole?:
    | 'executor'
    | 'applicant_for_letters_of_administration'
    | null;
  /** Non-proving LPR — the AI proposes `false` for a predeceased executor (LF-5380). */
  isProvingLpr?: boolean | null;
  /** Reason an LPR is non-proving; the AI proposes only a death or renunciation. */
  nonProvingReason?: NonProvingReason | null;
  isBeneficiary: boolean;
  /**
   * The PMS contact type chosen for this contact during matter creation, carried through the review
   * so the selection survives into the matter that is about to exist. Both halves travel together:
   * the label is stored with the id so the card can render the choice back with no catalogue call.
   * Absent means nothing was chosen, and the contact is simply not filed on the PMS matter.
   */
  pmsContactTypeId?: string | null;
  pmsContactTypeLabel?: string | null;
  matterNotes?: string | null;
  primaryIdentificationFileId?: number;
  secondaryIdentificationFileId?: number;
  /**
   * AI-extraction provenance carried through the create-matter wizard review
   * (LF-5597). Threaded from the preview diff row's `source`/`confidence` so the
   * review renders the "Extracted from …" footnote and echoes it back on apply
   * (persisted on any identity-variant created for this contact — LF-5578).
   */
  source?: QuoteSource;
  confidence?: ConfidenceLevel;
}

/**
 * Institution data embedded in inventory items
 */
export interface ExtractedInstitution {
  id: number;
  name: string;
  type: string;
  faviconUrl?: string | null;
}

/**
 * File type information from preview-changes endpoint
 */
export interface FileTypeDto {
  name: string;
  slug: string;
}

/**
 * File response from preview-changes endpoint
 * Rich file metadata including certified copy status
 */
export interface FileResponseDto {
  id: number;
  originalFilename: string | null;
  displayName: string | null;
  status: 'pending' | 'uploaded' | 'processing' | 'available' | 'archived';
  mimeType: string | null;
  imageMimeType?: string | null;
  isCertifiedCopy: boolean;
  entityId?: number | null;
  fileSizeKb: number | null;
  createdAt: string;
  updatedAt: string;
  fileType: FileTypeDto | null;
}

/**
 * Inventory item data from preview-changes endpoint
 */
/**
 * **How the AI matched this asset's institution** (VALE-1468).
 *
 * Review-only, computed once by the backend preview and never recomputed at apply —
 * apply resolves by identity and binds the id the reviewer confirmed (VALE-1428).
 *
 * **Read `confidence`; never re-derive it from `rung` or `score`.** The soft/confirmed
 * split is owned by `backend/src/institutions/utils/institution-match-rung.ts`, which
 * this package cannot import, so a second copy of the rule here is how the badge and
 * the matcher come to disagree. The score in particular is a trap: a `bsb-prefix` bind
 * never compared the name at all and a `first-word` bind scores its first word highly
 * by construction, so both carry a reassuring number for the wrong reason.
 */
export interface ExtractedInstitutionMatch {
  /** Which rung of the cascade bound it — informational; do not branch on it. */
  rung: string;
  /** Whether the reviewer should take a second look. `confirmed` is the golden path. */
  confidence: 'confirmed' | 'soft' | 'unmatched';
  /** The similarity the match cleared, for the rungs that computed one. */
  score?: number;
  /**
   * How many institutions the firm can already see whose names fold to this one. Only
   * ever set when nothing matched and applying would mint a new institution.
   */
  duplicateCandidateCount?: number;
  /**
   * The reviewed name literally answers several catalogue institutions of the
   * expected type (e.g. "Respect Aged Care"), so no soft rung bound it — apply
   * refuses to mint an institution from this row unless the reviewer picks the
   * specific one (setting `institutionId`) or explicitly confirms a new one
   * (`institutionConfirmedNew`). Never set alongside a bound `institutionId`.
   */
  ambiguous?: boolean;
}

export interface ExtractedInventoryItem {
  type: string;
  institutionId?: number;
  institution?: ExtractedInstitution;
  institutionName?: string;
  institutionEmail?: string;
  estateReference?: string;
  contactId?: number;
  linkedInventoryItemId?: number;
  itemInfo: Record<string, unknown>;
  /**
   * AI-extraction provenance carried through the create-matter wizard review
   * (LF-5597). Threaded from the preview diff row's `source`/`confidence` so the
   * review renders the "Extracted from …" footnote and echoes it back on apply
   * (drives LF-5317 auto-confirm + LF-5578 variant provenance).
   */
  source?: QuoteSource;
  confidence?: ConfidenceLevel;
  /**
   * VALE-784 — whose asset this appears to be, on a document naming more than
   * one person. Threaded off the preview diff wrapper by the same unwrap that
   * carries `source` / `confidence`, so the create-matter review can flag the
   * row exactly as the document-import review does. Review-only: it is never
   * echoed back on apply, because nothing persists it.
   */
  holderAttribution?: HolderAttribution;
  /**
   * Value-confirmation intent (VALE-734), set by the review card's "Confirmed
   * against a source" control before the item is persisted. Same intent shape
   * `CreateInventoryItemRequest` already accepts on create (VALE-355) — never
   * the persisted read model, since nothing is persisted yet.
   */
  currentValueConfirmation?: { source?: string; sourceFileId?: number } | null;
  dodValueConfirmation?: { source?: string; sourceFileId?: number } | null;
  /**
   * How the institution on this row was matched (VALE-1468). Review-only: the backend
   * ignores it on apply, like every other display-only field on the preview DTO.
   */
  institutionMatch?: ExtractedInstitutionMatch;
  /**
   * Apply-time only, echoed back on the payload — ignored on the preview
   * response. Set when the reviewer went through the institution picker's
   * "Add anyway"/"Add new institution" step for this row, so apply mints an
   * institution from `institutionName` even when `institutionMatch.ambiguous`
   * is true (VALE-1753).
   */
  institutionConfirmedNew?: boolean;
}

/**
 * Intermediate shape used by the review stage. Derived from
 * `PreviewChangesResponse` — items with `action: 'create'`, unwrapped from
 * `proposed` (see `use-ai-document-ingestion.ts`).
 */
export interface EntityPreviewData {
  matter: ExtractedMatterDataFromPreview;
  contacts: ExtractedContact[];
  inventoryItems: ExtractedInventoryItem[];
  /** All files associated with this matter (for display only) */
  files?: FileResponseDto[];
  /**
   * AI-proposed entitlements extracted from the will (may be empty). Carried
   * through the create-matter review stage so it can render the review
   * surface and forward to apply-changes.
   */
  entitlements?: PreparedEntitlement[];
  /** Signals the matter already has entitlements; accept flow should be blocked. */
  entitlementsPreviewWarning?: EntitlementsPreviewWarning | null;
  /** Clauses extracted but not importable — surfaced for manual entry. */
  droppedEntitlements?: DroppedEntitlement[];
  /** Class-resolution proposals (will-only). Omitted when empty. */
  proposedClassResolutions?: PreparedClassResolution[];
  /** Handovers the documents stated (VALE-1866). Omitted when none. */
  proposedHandovers?: PreviewProposedHandover[];
  /** Contacts already on the matter a reviewer may pick as a handler. */
  handoverHandlerOptions?: HandoverHandlerOption[];
}

/**
 * File progress tracking for individual documents
 */
export interface FileProgress {
  id: number;
  originalFilename: string | null;
  status: string;
  identifiedType?: string;
  confidence?: 'High' | 'Medium' | 'Low';
  processingDurationMs?: number;
  error?: string;
}

/**
 * Machine-readable classification of an AI-analysis failure, mirrored from the
 * backend `AnalysisFailureReason`. Drives cause-specific failure copy.
 *  - `LLM_PROVIDER_UNAVAILABLE`: transient AI-provider outage (rate-limit / overload) — no-blame copy.
 *  - `UNKNOWN`: any other failure — generic copy.
 * Typed as a widened string union so a future backend code never breaks the build.
 */
export type AnalysisFailureReason =
  | 'LLM_PROVIDER_UNAVAILABLE'
  | 'UNKNOWN'
  | (string & {});

/**
 * Progress tracking for AI document analysis and consolidation
 */
export interface AIAnalysisProgress {
  matterId: number;
  // `retrying` (LF-4320) is non-terminal: every LLM provider is momentarily throttled
  // and the backend is sleeping before a delayed re-attempt on a fresh quota window
  // (LF-4319). Treated as in-flight — the UI shows the "taking longer than usual"
  // treatment, not the hard-error screen.
  status: 'processing' | 'retrying' | 'consolidating' | 'completed' | 'failed';
  message?: string; // Underlying technical failure message when status is 'failed'
  reasonCode?: AnalysisFailureReason; // Machine-readable failure classification when status is 'failed'
  overallProgress: number; // Percentage 0-100
  totalFiles: number;
  processedFiles: number;
  files: FileProgress[];
  estimatedTimeRemaining?: number;
  extractedData?: unknown;
  completedAt?: string;
  pendingFiles?: number;
  readyFiles?: number;
  consolidationSteps?: string[];
}

// ============================================================================
// Shared taxonomies (LF-2910)
//
// Source of truth for cross-flow enums and provenance types. Re-exported by
// `lib/services/recordings/types.ts` so AI Scribe, AI Doc-Intel, and Client
// Intake Form all import from one place.
// ============================================================================

/** LLM confidence on an individual extracted field/entity. */
export type ConfidenceLevel = 'high' | 'medium' | 'low';

/**
 * Classification the LLM assigns to extracted instructions.
 *
 * The **const is the source of truth and the type is derived from it**, so a
 * guard that needs to enumerate the classifications reads a real list rather
 * than a hand-typed copy of this union (VALE-1703). Mirrors
 * `INSTRUCTION_TYPE_VALUES` in `backend/src/ai-document-intelligence/types/
 * ai-provenance.types.ts`, which is the wire contract's own source of truth.
 */
export const INSTRUCTION_TYPE_VALUES = [
  'action',
  'hold_delay',
  'asset_instruction',
  'pending_decision',
  'client_self_action',
  'risk_flag',
  'selective_action',
  'entitlement_distribution',
  'factual_correction'
] as const;

export type InstructionType = (typeof INSTRUCTION_TYPE_VALUES)[number];

/** Slug enum for the LPR role on an extracted contact. */
export type LegalPersonalRepresentativeRoleSlug =
  | 'executor'
  | 'applicant_for_letters_of_administration';

/**
 * Quote-source discriminated union. One variant per row based on where the
 * quote came from — transcript (scribe), document page (doc-intel / intake
 * file uploads), or intake form answer. The `quote` field carries the
 * verbatim passage.
 */
export type QuoteSource =
  | { type: 'transcript'; quote: string }
  | { type: 'document'; quote: string; fileId: number; page?: number }
  | { type: 'form'; quote: string; fieldId: string };

/** Standard per-row provenance trio. Mix into any diff row. */
export interface ProvenanceFields {
  confidence?: ConfidenceLevel;
  /** Present on inventory / checklist / existingItemNote / matter rows. */
  instructionType?: InstructionType;
  source?: QuoteSource;
}

/** Whether the names printed against a holding are the deceased's. */
export type HeldByDeceased = 'yes' | 'no' | 'unclear';

/**
 * Whose asset is this — set when the source document names more than one
 * person (VALE-784), typically a third-party financial statement naming both
 * the deceased and the surviving spouse who is also the executor.
 *
 * Mirrors the backend `HolderAttribution`. Two things it is NOT:
 *
 * - It is **not** `isNonEstate`. That says the *deceased* held an interest
 *   passing outside the estate; this says the holding may not be the
 *   deceased's at all. Different fact, different person.
 * - Its **absence is not a clearance**. Most documents name one party and
 *   raise no question, so `undefined` means "never asked", not "confirmed as
 *   the deceased's". Never render an absent attribution as reassurance.
 */
export interface HolderAttribution {
  /** Holder name(s) printed against this holding, verbatim from the document. */
  namedHolders: string[];
  heldByDeceased: HeldByDeceased;
  /** Plain-English rationale for the reviewer (≤200 chars). */
  rationale?: string;
}

/**
 * The single predicate for "surface this row as possibly someone else's",
 * mirroring the backend's `isHolderAttributionFlagged`. Every surface routes
 * through this rather than re-testing the enum, so the two review screens can
 * never disagree about which rows are flagged.
 *
 * `unclear` counts as flagged: an un-flagged row is imported by default, so
 * treating "could not tell" as "the deceased's" is exactly the silent
 * inclusion this exists to prevent.
 */
export function isHolderAttributionFlagged(
  attribution: HolderAttribution | undefined
): boolean {
  return attribution !== undefined && attribution.heldByDeceased !== 'yes';
}

/**
 * Reviewer-facing one-liner naming who the document says holds the asset.
 * Shared by both review screens so the wording cannot drift between them.
 */
export function formatHolderAttribution(
  attribution: HolderAttribution
): string {
  const names = attribution.namedHolders.filter(Boolean);
  if (attribution.heldByDeceased === 'unclear') {
    return names.length > 0
      ? `The named holder is ${names.join(', ')}, and the document does not make clear whether this belonged to the deceased.`
      : 'The document does not make clear whether this belonged to the deceased.';
  }
  return names.length > 0
    ? `Held in the name of ${names.join(', ')}, not the deceased.`
    : 'This appears to belong to someone other than the deceased.';
}

// ============================================================================
// New Endpoints: preview-changes & apply-changes
// ============================================================================

/**
 * Match confidence when deduplicating against existing matter entities
 */
export type MatchConfidence = 'exact' | 'high' | 'medium' | 'low';

/**
 * Field-level change between existing and proposed entity values.
 * Present in fieldChanges when action is 'update'.
 */
export interface FieldChange {
  /** Current value on the existing entity (null if empty/missing) */
  existing: unknown;
  /** Value from the AI extraction */
  proposed: unknown;
  /** 'enrichment': existing is empty, proposed adds a new value.
   *  'conflict': both sides have values, they differ.
   *  'addition': a recorded note is added to, never replaced. `proposed` is
   *  ONLY the text being added, and apply appends it to `existing` (VALE-1951). */
  changeType: 'enrichment' | 'conflict' | 'addition';
  /** Which value the backend recommends accepting */
  recommended: 'proposed' | 'existing';
}

// ─── Preview-changes: matter / deceased ──────────────────────────────

/** Un-prefixed deceased address shape used on preview responses. */
export interface PreviewDeceasedAddress {
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

/** Un-prefixed deceased shape used on preview responses. */
export interface PreviewDeceased {
  title?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string;
  dateOfDeath?: string;
  address?: PreviewDeceasedAddress;
  /** Matter-level free-text notes (AI-extracted estate context). Writes to `Matter.notes` on apply. */
  notes?: string;
  /** Matter-level grant primary reference / application number (from the grant extraction). Writes to `Matter.grantOfRepresentationReference` on apply. */
  grantOfRepresentationReference?: string;
  /** Matter-level grant secondary code / unique identifier (from the grant extraction). Writes to `Matter.grantOfRepresentationCode` on apply. */
  grantOfRepresentationCode?: string;
}

/**
 * Matter diff structure from preview-changes.
 *
 * Per LF-2910 aligned on the AI Scribe shape: un-prefixed fields
 * (`firstName`, not `decedentFirstName`), per-field `fieldChanges` +
 * `existingSnapshot` for accept/reject UX, and provenance trio.
 */
/**
 * Role a `nameVariants` entry plays for a person (LF-5682). Mirrors the backend's
 * AI-extractable subset — `legal_at_death` is deliberately absent, since the canonical
 * name already carries that role.
 */
export type ExtractedNameVariantRole =
  | 'will_name'
  | 'former_maiden'
  | 'married'
  | 'trading'
  | 'other_aka';

/**
 * Why a name variant differs from the canonical name (LF-5682). Deliberately excludes
 * `formal_change_with_evidence`: that value makes the affidavit assert that evidence of a
 * change of name is annexed, which only the lawyer can confirm, so it is set by hand on the
 * entity screen and never by extraction.
 */
export type ExtractedReconciliationBasis = 'informal_aka' | 'spelling_variant';

/** An alternate name the documents give a person (LF-5682). */
export interface ExtractedNameVariant {
  value: string;
  role: ExtractedNameVariantRole;
  reconciliationBasis?: ExtractedReconciliationBasis;
}

/** An alternate address the documents give a person (LF-5682). */
export interface ExtractedAddressVariant {
  value: PreviewDeceasedAddress;
  type: 'residential' | 'postal';
  /** False when the documents show the address was superseded. Defaults to true. */
  isCurrent?: boolean;
  periodNote?: string;
}

export interface PreviewMatterDiff extends ProvenanceFields {
  /**
   * What the AI did to the matter's deceased.
   *
   * **`'create'` never arrives on the wire** (VALE-1792). Both producers pin
   * their DTO to `@IsIn(['skip', 'update'])` — doc-intel's `PreviewMatterDiff`
   * and AI Scribe's — because both diff against a matter, and a matter the
   * preview has no row for is diffed against an empty snapshot rather than
   * skipped. It is here for the client-SYNTHESISED matterless case:
   * `synthesisePreviewFromExtractedData` builds a preview from a recording's
   * raw extraction with no matter behind it at all, and says `create` there for
   * the same reason it says it on every contact and inventory row beside it.
   *
   * The distinction is load-bearing rather than cosmetic, because it is the
   * only thing `itemChangeKind` can read: an `update` carrying no
   * `fieldChanges` is `unchanged`, which takes no ink and no badge. A
   * matterless deceased labelled `update` therefore renders as plain text
   * beside contacts that ink blue and carry `NEW`.
   */
  action: 'create' | 'skip' | 'update';
  proposed: PreviewDeceased;
  /** Present when action is 'skip'. */
  skipReason?: string;
  /**
   * Field-level changes — present only when action is 'update'.
   * Keys are dot-notation: 'firstName', 'address.postcode'.
   */
  fieldChanges?: Record<string, FieldChange>;
  /** Snapshot of existing matter deceased details at preview time. */
  existingSnapshot?: PreviewDeceased;
  /**
   * Alternate names / addresses for the deceased (LF-5682). Carried alongside `proposed`
   * rather than inside it, so they stay out of the field-level diff. Not rendered — the
   * reviewer corrects them post-apply on the entity screen — but they MUST be forwarded
   * verbatim onto the apply payload's `deceased` block, or the extraction is silently lost.
   */
  nameVariants?: ExtractedNameVariant[];
  addressVariants?: ExtractedAddressVariant[];
}

/**
 * Where the match decision came from. Drives the diff-row trust badge.
 *
 * - `matcher`: deterministic identifier match (BSB+account, SRN, contact name
 *   fuzzy, etc.). Status quo behaviour; highest deterministic confidence.
 * - `llm-hint`: the LLM emitted `matchedExistingId` and the backend validator
 *   accepted it — the deterministic matchers stayed silent. Covers
 *   cross-type / sparse-entity / free-text variants the matcher cannot see
 *   (the LF-3102 cross-type case lives here).
 * - `matcher+llm-hint`: both agree. Highest combined confidence.
 *
 * Absent on `create` rows. Absent on pre-LF-3330 stored blobs — treat as
 * `'matcher'` for legacy rendering.
 */
export type MatchSource = 'matcher' | 'llm-hint' | 'matcher+llm-hint';

/**
 * Contact diff structure from preview-changes
 */
export interface ContactDiff extends ProvenanceFields {
  action: 'create' | 'skip' | 'update';
  proposed: ExtractedContact;
  skipReason?: string;
  /**
   * Role-bearing alternate names for this contact (LF-5682). Carried alongside `proposed`
   * rather than inside it, so they stay out of the field-level diff. Not rendered — the
   * reviewer corrects them post-apply on the entity screen — but they MUST be forwarded
   * verbatim onto the apply row, or the extraction is silently lost.
   */
  nameVariants?: ExtractedNameVariant[];
  /**
   * Alternate addresses for this contact (VALE-1754) — an address a document gave for them
   * that is not the recorded one. Same handling as `nameVariants` above: not rendered, but
   * forwarded verbatim or the extraction is silently lost.
   */
  addressVariants?: ExtractedAddressVariant[];
  matchedExistingId?: number;
  matchConfidence?: MatchConfidence;
  /** LF-3330 — where the match decision came from. */
  matchSource?: MatchSource;
  /** LF-3330 — LLM rationale for the hint. Carried on the wire; no longer surfaced in the UI (VALE-720 removed the match tag). */
  matchReasoning?: string;
  /** Field-level changes — present only when action is 'update' */
  fieldChanges?: Record<string, FieldChange>;
  /** Snapshot of existing entity at preview time — present only when action is 'update' */
  existingSnapshot?: Partial<ExtractedContact>;
  /**
   * LF-5242 — latent "separate as a new contact" payload. Present on a matched row
   * (update/skip) only when the match is NOT corroborated by an exact name/email
   * signal. When present, the reviewer may split the row into a new contact; on
   * apply the frontend forwards `proposed` as an ordinary `{ action: 'create' }`
   * row (this wrapper never enters the apply request).
   */
  separateAsCreate?: { proposed: ExtractedContact };
}

/**
 * Inventory item diff structure from preview-changes
 */
export interface InventoryItemDiff extends ProvenanceFields {
  action: 'create' | 'skip' | 'update';
  proposed: ExtractedInventoryItem;
  skipReason?: string;
  matchedExistingId?: number;
  matchConfidence?: MatchConfidence;
  /** LF-3330 — where the match decision came from. */
  matchSource?: MatchSource;
  /** LF-3330 — LLM rationale for the hint. Carried on the wire; no longer surfaced in the UI (VALE-720 removed the match tag). */
  matchReasoning?: string;
  /** Field-level changes — present only when action is 'update' */
  fieldChanges?: Record<string, FieldChange>;
  /** Snapshot of existing entity at preview time — present only when action is 'update' */
  existingSnapshot?: Partial<ExtractedInventoryItem>;
  /**
   * VALE-784 — whose asset this appears to be, on a document naming more than
   * one person. Rides the wrapper, not `proposed`: it describes the extraction,
   * not the inventory item, and `proposed` is forwarded to an apply endpoint
   * that rejects undeclared fields.
   */
  holderAttribution?: HolderAttribution;
}

/**
 * File diff structure from preview-changes
 */
export interface FileDiff {
  fileId: number;
  originalFilename: string | null;
  currentType: string;
  proposedType: string;
  action: 'reclassify' | 'none';
  /**
   * Whether this document is a certified copy — the same flag the Documents tab
   * reads to render its "Certified Copy" badge (VALE-1011). Carried on the diff
   * so the AI review surfaces do not have to say something different about the
   * same document: whether a death certificate or an identity document is
   * certified changes what the outgoing institution letter may say about it.
   */
  isCertifiedCopy: boolean;
  /**
   * Set when this document could not be read, so NONE of its contents are
   * reflected anywhere in this preview. Undefined on every document that was read.
   *
   * The reviewer on this screen is deciding whether to accept AI-extracted data
   * into the matter; a document that was silently skipped makes that a decision
   * taken on an incomplete picture, which is why it is surfaced here and not only
   * on the Documents tab.
   */
  analysisFailureReasonCode?: AnalysisFailureReasonCode;
}

/**
 * AI-extracted entitlement proposal. Beneficiary and item references are by
 * **index** into the adjacent `contacts` / `inventoryItems` arrays in the
 * preview response, not by persisted id — resolution to real ids happens
 * server-side during apply, after contacts and items are created.
 *
 * A beneficiary row is either named (`contactIndex` set) or class-shaped
 * (`isClassBeneficiary` true + `classRelationship` + `classDistributionMode`).
 * Class rows never carry `contactIndex`; the backend translates
 * `classRelationship` to a real `entitlementClassId` at apply time via
 * lookup-or-create.
 */
export interface PreparedEntitlementBeneficiary {
  /** Set on named rows; index into the preview's `contacts` array. */
  contactIndex?: number;
  /** True on class rows. */
  isClassBeneficiary?: boolean;
  /** Required when `isClassBeneficiary` is true. */
  classRelationship?: import('@/lib/services/entitlements/types').BeneficiaryClassRelationship;
  /** Required when `isClassBeneficiary` is true. */
  classDistributionMode?: import('@/lib/services/entitlements/types').ClassDistributionMode;
  amount?: string;
  percentage?: string;
  fractionNumerator?: number;
  fractionDenominator?: number;
  substituteTrigger?: import('@/lib/services/entitlements/types').SubstituteTrigger;
  conditionNote?: string;
  isLifeInterest?: boolean;
  sortOrder?: number;
}

/**
 * A proposed resolution for an unnamed class referenced by the will. Used in
 * both the preview response (`proposedClassResolutions`) and the apply
 * request (`acceptedClassResolutions`). Member references are by index into
 * the preview's `contacts` array — the backend translates the
 * `classRelationship` to a real `entitlementClassId` via lookup-or-create.
 */
export interface PreparedClassResolution {
  classRelationship: import('@/lib/services/entitlements/types').BeneficiaryClassRelationship;
  /** Indices into the preview's `contacts` array. */
  memberContactIndices: number[];
  /**
   * Preview-only (VALE-1029). True when the matter already carries a confirmed
   * membership for this class. The review card renders such a proposal excluded
   * by default, because applying it would replace a member set someone has
   * already curated.
   */
  alreadyResolved?: boolean;
  /**
   * Preview-only (VALE-1029). Display names of the members the class currently
   * holds, in member order. Present only alongside `alreadyResolved`.
   */
  existingMemberNames?: string[];
  /**
   * Apply-only (VALE-1029). The reviewer's explicit opt-in to replace an
   * already-confirmed membership. Set only when they included a proposal the
   * preview flagged as `alreadyResolved`; the backend leaves a resolved class
   * untouched without it.
   */
  overwriteResolved?: boolean;
}

export interface PreparedEntitlementItem {
  itemIndex: number;
  quantity?: string;
  sortOrder?: number;
}

export interface PreparedEntitlement {
  type: 'pecuniary_legacy' | 'specific_gift' | 'residuary';
  notes?: string;
  sortOrder?: number;
  beneficiaries: PreparedEntitlementBeneficiary[];
  items?: PreparedEntitlementItem[];
  /**
   * What is being given, in words — a specific gift on a matter type with no
   * inventory model (estate planning), where there is no inventory item to point
   * at. Render this as the gift's subject when there are no `items`.
   */
  subjectDescription?: string;
  /**
   * Which testator's Will this clause comes from, as a slot: the planning
   * testator rows do not exist until apply creates them. Echo it back on the
   * apply request untouched — apply resolves it to a real id.
   */
  planningTestatorSlot?: PlanningTestatorSlot;
  /**
   * What the import will do with this clause, decided per clause against the
   * entitlements the matter already holds (VALE-1030). Absent on a matter with
   * no entitlements, where every clause is imported.
   *
   * Advisory. Apply re-derives it against the rows as they stand in its own
   * transaction, so this is what the review page SAYS will happen, not what
   * decides it. Echo it back untouched.
   */
  matchAction?: 'create' | 'skip';
  /** The existing entitlement this clause restates. Only on a `skip`. */
  matchedExistingId?: number;
  /**
   * Why a matched clause is not imported. `differs_from_existing` is the one
   * that asks something of the lawyer: this document and the matter disagree
   * about a gift, and the matter's version is the one that stands.
   */
  matchReason?: 'already_recorded' | 'differs_from_existing';
}

/** Which of the (at most two) people a planning matter is for. */
export type PlanningTestatorSlot = 'client' | 'partner';

export type PlanningAppointmentRole =
  | 'executor'
  | 'substitute_executor'
  | 'guardian'
  | 'attorney'
  | 'medical_decision_maker';

export type PlanningDocumentKind =
  | 'will'
  | 'enduring_power_of_attorney'
  | 'medical_treatment_decision_maker';

export type PlanningSupersedeIntent = 'supersede' | 'retain' | 'unknown';

/** A person a planning fact refers to — a name, plus an optional preview-array index hint. */
export interface PreparedPlanningPerson {
  name: string;
  contactIndex?: number;
}

export interface PreparedPlanningAppointment extends PreparedPlanningPerson {
  role: PlanningAppointmentRole;
  relationship?: string;
  sortOrder?: number;
  /** Free text about the appointment as the client wrote it (the form's own "Role" answer). */
  notes?: string;
}

export interface PreparedPlanningDocumentStatus {
  documentKind: PlanningDocumentKind;
  /**
   * Three-valued, and the null is load-bearing: `null` is "not established
   * yet" ("I think I made one in the nineties"), which is NOT the same as
   * `false`. Render the three states distinctly.
   */
  hasExisting?: boolean | null;
  madeInYear?: number | null;
  storageLocation?: string | null;
  supersedeIntent?: PlanningSupersedeIntent | null;
}

export interface PreparedPlanningWish {
  category?: string;
  text: string;
  sortOrder?: number;
}

export interface PreparedPlanningDependant extends PreparedPlanningPerson {
  relationship?: string;
  dateOfBirth?: string;
  /** Omitted = shared between both testators, which is the common case. */
  belongsToTestatorSlot?: PlanningTestatorSlot;
  isDependant?: boolean;
  sortOrder?: number;
}

export interface PreparedPlanningTestator extends PreparedPlanningPerson {
  slot: PlanningTestatorSlot;
  appointments: PreparedPlanningAppointment[];
  documentStatuses: PreparedPlanningDocumentStatus[];
  wishes: PreparedPlanningWish[];
}

/**
 * The planning-facts proposal for an estate-planning matter — read-only in the
 * review, and echoed back on the apply request unchanged.
 *
 * **People are named, not indexed**, so unlike `entitlements` this block needs
 * no contact-index remapping when the reviewer excludes or reorders a contact
 * row: apply resolves each name against the contacts it actually wrote, and a
 * name that resolves to nothing is recorded as an unresolved person for the
 * solicitor to place rather than dropped.
 */
export interface PreparedPlanningFacts {
  testators: PreparedPlanningTestator[];
  /** Matter-scoped, named once — a couple's children are one family. */
  dependants: PreparedPlanningDependant[];
}

export type EntitlementsPreviewWarning = 'matter_has_existing_entitlements';

/**
 * A clause the AI extracted from the will but could not stage for import —
 * because applying it would be rejected (a gift that never said what is being
 * given, shares that do not add up, a second whole-residue clause). The
 * structured data is deliberately not carried: it cannot be applied without
 * resolving the underlying problem, so the reviewer is pointed at manual entry
 * rather than invited to round-trip a payload apply still rejects.
 *
 * Surfacing these is the whole point — a dropped clause produces no card, no
 * row, and no other trace on the review screen, so without a callout the
 * lawyer accepts an import silently missing part of the will.
 */
export interface DroppedEntitlement {
  /** The kind of gift the AI proposed (residuary, specific_gift, …). */
  entitlementType: string;
  /** Machine-readable cause, e.g. `SPECIFIC_GIFT_REQUIRES_ITEMS`. */
  rule: string;
  /** The will's own wording for the clause, when the AI captured it. */
  notes?: string;
  /** Disambiguates when several clauses are dropped at once. */
  beneficiaryCount: number;
}

/**
 * Preview changes response from GET /preview-changes
 * Returns diff structure with deduplication applied.
 *
 * Note: `matter` is optional — absent when no deceased info was extracted.
 */
export interface PreviewChangesResponse {
  matter?: PreviewMatterDiff;
  contacts: ContactDiff[];
  inventoryItems: InventoryItemDiff[];
  files: FileDiff[];
  /** AI-proposed entitlements extracted from the will (may be empty) */
  entitlements?: PreparedEntitlement[];
  /** Signals the matter already has entitlements; accept flow should be blocked */
  entitlementsPreviewWarning?: EntitlementsPreviewWarning | null;
  /** Clauses extracted but not importable — surfaced for manual entry. */
  droppedEntitlements?: DroppedEntitlement[];
  /**
   * Class-resolution proposals — present when the will references a class
   * AND the consolidation context found candidate members. Omitted when
   * empty. Independent of the per-entitlement include/exclude toggle.
   */
  proposedClassResolutions?: PreparedClassResolution[];
  /**
   * Short, scannable bullet summary of the non-structured context the entity
   * cards drop (will reasoning, free-text "anything else", urgent matters,
   * relationship nuance). Rendered read-only as "Key Notes".
   *
   * Two producers, one render: a client intake form, and (VALE-995) an AI
   * Document Import on a **non-estate** matter, where the preview gates leave
   * contacts as the only structured output the matter type can hold. Absent on
   * an estate-administration document import, and when there was no
   * non-structured context worth surfacing.
   */
  narrativeOverview?: string[];
  /**
   * Estate-planning only, and only for a firm on the `estate-planning-suite`
   * rollout: the planning-facts proposal, grouped per testator. Absent on every
   * other matter type.
   */
  planningFacts?: PreparedPlanningFacts;
  /**
   * Handovers the documents or intake form stated (VALE-1866). Proposals only;
   * absent when there are none or the firm is off the External Handling rollout.
   */
  proposedHandovers?: PreviewProposedHandover[];
  /** Contacts already on the matter a reviewer may pick as a handler. */
  handoverHandlerOptions?: HandoverHandlerOption[];
  /** Handovers the matter already records, on the rows they cover. */
  currentHandovers?: CurrentHandover[];
  /**
   * Side observations about records the matter already holds, each already
   * settled to what the record's note does not say (VALE-2007). The same shape
   * AI Scribe emits, because the backend reuses Scribe's DTO for it. Absent
   * when there are none.
   */
  existingItemNotes?: import('@/lib/services/recordings/types').AiScribeExistingItemNote[];
  summary: {
    contacts: { new: number; updated: number; skipped: number };
    inventoryItems: { new: number; updated: number; skipped: number };
    files: { reclassify: number; unchanged: number };
    matter: { updated: boolean };
    checklistItems?: { new: number };
    existingItemNotes?: { new: number };
    entitlements?: { new?: number };
  };
}

// ─── Apply-changes: matter / deceased ─────────────────────────────────

/** Un-prefixed deceased address shape used on apply requests. */
export interface ApplyDeceasedAddress {
  streetAddress?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: string;
}

/**
 * Un-prefixed deceased shape used on apply requests. Merge-semantic on the
 * backend — provided fields are merged onto existing decedent data,
 * undefined / null / empty-string fields are ignored.
 */
export interface ApplyMatterDeceased {
  title?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  /** ISO YYYY-MM-DD */
  dateOfBirth?: string;
  /** ISO YYYY-MM-DD */
  dateOfDeath?: string;
  /** Merge-semantic — merged onto existing.decedentAddress. */
  address?: ApplyDeceasedAddress;
  /**
   * Alternate names / addresses for the deceased, forwarded verbatim from the preview
   * matter diff (LF-5682). Persisted as person_name_variant / address_variant rows.
   */
  nameVariants?: ExtractedNameVariant[];
  addressVariants?: ExtractedAddressVariant[];
  /**
   * Matter-level provenance echoed from the preview diff row (LF-5682) — persisted on the
   * identity variants above. Does not affect the deceased scalar fields.
   */
  confidence?: ConfidenceLevel;
  source?: QuoteSource;
}

/**
 * User-role assignment (which law-firm user is the acting solicitor / an
 * assisting team member on the matter). Shape matches the backend
 * `src/matters/roles/dto/role-assignment.dto.ts`.
 */
export interface RoleAssignmentDto {
  userId: number;
  role: 'acting_solicitor' | 'assisting_member';
}

/**
 * Apply contact DTO for POST /apply-changes
 */
export interface ApplyContactDto {
  action: 'create' | 'update';
  proposed: ExtractedContact;
  /** Required when action is 'update' — ID of existing contact to merge into */
  matchedExistingId?: number;
  /** Per-contact extraction confidence, echoed from the preview diff row (LF-5578). */
  confidence?: ConfidenceLevel;
  /**
   * Provenance source, echoed from the preview diff row (LF-5578). Persisted (full
   * QuoteSource) on any identity variant (e.g. a contact-alias name variant) created on apply.
   */
  source?: QuoteSource;
  /**
   * Role-bearing alternate names, echoed from the preview diff row (LF-5682). Not rendered;
   * must be forwarded verbatim or the extraction is silently lost.
   */
  nameVariants?: ExtractedNameVariant[];
  /**
   * Alternate addresses, echoed from the preview diff row (VALE-1754). Persisted as
   * address_variant rows owned by this contact. Same forward-or-lose rule as above.
   */
  addressVariants?: ExtractedAddressVariant[];
}

/**
 * Apply inventory item DTO for POST /apply-changes
 */
export interface ApplyInventoryItemDto {
  action: 'create' | 'update';
  proposed: ExtractedInventoryItem;
  /** Required when action is 'update' — ID of existing inventory item to merge into */
  matchedExistingId?: number;
  /**
   * Per-item extraction confidence, echoed from the preview diff row (LF-5317).
   * Gates AI auto-confirm of the current value on import (HIGH + document source).
   */
  confidence?: ConfidenceLevel;
  /**
   * Provenance source, echoed from the preview diff row (LF-5317). On a
   * HIGH-confidence, document-sourced item the fileId is attached as the
   * value-confirmation source when the value is stamped on import.
   */
  source?: QuoteSource;
}

/**
 * Apply file DTO for POST /apply-changes
 */
export interface ApplyFileDto {
  fileId: number;
  proposedType: string;
}

/**
 * Request body for POST /apply-changes.
 *
 * Per LF-2910: flat root matches the AI Scribe shape. `deceased` is
 * merge-semantic — omit when the reviewer rejected all matter changes.
 */
export interface ApplyChangesRequest {
  deceased?: ApplyMatterDeceased;
  contacts: ApplyContactDto[];
  inventoryItems: ApplyInventoryItemDto[];
  files: ApplyFileDto[];
  /** Doc-intel / intake only — file-link fields. */
  deathCertificateFileId?: number;
  willFileId?: number;
  grantOfRepresentationFileId?: number;
  lawFirmInternalReference?: string;
  /** Reviewed grant primary reference / application number. Writes to `Matter.grantOfRepresentationReference`. */
  grantOfRepresentationReference?: string;
  /** Reviewed grant secondary code / unique identifier. Writes to `Matter.grantOfRepresentationCode`. */
  grantOfRepresentationCode?: string;
  roles?: RoleAssignmentDto[];
  /** Free-text matter notes — lawyer-authored estate context captured during AI matter creation. */
  notes?: string;
  /** Explicit matter status transition (typically `In Progress` when applying AI-extracted data for a new matter). */
  status?: 'Draft' | 'In Progress' | 'Completed';
  /** Reviewed entitlement proposals (same shape as preview's PreparedEntitlement[]) */
  entitlements?: PreparedEntitlement[];
  /**
   * Class resolutions the lawyer accepted in the review screen. Excluded
   * proposals are simply omitted from this array — there is no separate
   * `rejectedClassResolutions` field. Member references index into the
   * sibling `contacts` array.
   */
  acceptedClassResolutions?: PreparedClassResolution[];
  /**
   * The planning facts to write, echoed back from the preview. Appended, never
   * replaced, so accepting this proposal cannot delete facts another source
   * contributed.
   */
  planningFacts?: PreparedPlanningFacts;
  /** Proposed handovers the reviewer accepted (VALE-1866). */
  acceptedHandovers?: ApplyProposedHandover[];
  /**
   * The side observations the reviewer kept, with their text as the reviewer
   * left it (VALE-2007). Each is appended to its record's note, adding only
   * what the note does not already say.
   */
  existingItemNotes?: import('@/lib/services/recordings/types').ApplyExistingItemNote[];
  /** Bypasses matter uniqueness validation (`lawFirmInternalReference` + deceased identity) when the lawyer confirms a legitimate duplicate. */
  allowDuplicate?: boolean;
}

/**
 * Response from POST /apply-changes
 */
export interface ApplyChangesResponse {
  matterId: number;
  applied: {
    matter: { updated: boolean };
    contacts: { created: number; updated: number };
    inventoryItems: { created: number; updated: number };
    files: { reclassified: number };
    entitlements?: {
      created: number;
      /** Accepted clauses that produced nothing because a reference could not be resolved. */
      skipped?: number;
      /** Clauses the matter already records, matched per clause (VALE-1030). */
      matchedExisting?: number;
      /**
       * Matched clauses this document states differently from the matter's own
       * version. Not imported: an apply never overwrites the lawyer's clause.
       */
      differsFromExisting?: number;
    };
    classResolutions?: { applied: number };
    /** Absent when no handover was accepted (VALE-1866). */
    handovers?: HandoversApplyOutcome;
    planningFacts?: {
      testators: number;
      appointments: number;
      documentStatuses: number;
      wishes: number;
      dependants: number;
    };
  };
}
